/**
 * `useColumnReorder` — the preset's U5 column-reordering wiring: the drag-and-drop context
 * inputs (`columnIds` + sensors + drop handler) and the keyboard "Move left/right" controls
 * the header menu surfaces, both driven by the SAME `applyColumnReorder` so a drag and a menu
 * move can't diverge.
 *
 * Off (`enabled: false`) it returns `undefined` for both, and every header falls back to no
 * move group. The hooks below still run unconditionally — they are hooks, they are cheap, and
 * the DnD context that consumes them only mounts when reordering is on.
 */
import { useEvent } from 'react-use-event-hook'
import { type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { RowData, Table } from '@tanstack/react-table'

import type { ColumnHeaderMenuMove } from './primitives/column-header-menu'
import { readColumnMeta } from './primitives/column-meta'
import { applyColumnReorder, reorderColumnsFromDragEnd } from './primitives/column-reorder'
import type { DataTableHeaderReorder } from './primitives/data-table-header-row'

export type UseColumnReorderOptions<TData extends RowData> = {
  table: Table<TData>
  enabled: boolean
}

export type ColumnReorder = {
  /** The header row's DnD wiring, or `undefined` when reordering is off. */
  columnReorder: DataTableHeaderReorder | undefined
  /** Keyboard move controls for one column; `undefined` for a non-reorderable one. */
  getColumnMove: (columnId: string) => ColumnHeaderMenuMove | undefined
}

export const useColumnReorder = <TData extends RowData>({
  table,
  enabled,
}: UseColumnReorderOptions<TData>): ColumnReorder => {
  // Only NON-pinned, non-opted-out visible columns are reorder targets; a pinned column holds
  // its region and a `meta.enableReordering: false` column stays put.
  const reorderableColumnIds = enabled
    ? table
        .getCenterVisibleLeafColumns()
        .filter((column) => readColumnMeta(column)?.enableReordering !== false)
        .map((column) => column.id)
    : []
  const columnDragSensors = useSensors(
    // A small activation distance so a plain click still sorts; only a real drag reorders.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )
  const handleColumnDragEnd = useEvent((event: DragEndEvent) => {
    reorderColumnsFromDragEnd(table, event)
  })

  // Keyboard reorder (R5/R8): "Move left/right" controls for a reorderable column, wired to the
  // same `applyColumnReorder` the drag uses — swap the column with its neighbor in the
  // reorderable (center) set.
  const getColumnMove = (columnId: string): ColumnHeaderMenuMove | undefined => {
    if (!enabled) {
      return undefined
    }
    const index = reorderableColumnIds.indexOf(columnId)
    if (index === -1) {
      return undefined
    }
    const leftNeighbor = reorderableColumnIds[index - 1]
    const rightNeighbor = reorderableColumnIds[index + 1]

    return {
      canMoveLeft: leftNeighbor !== undefined,
      canMoveRight: rightNeighbor !== undefined,
      onMoveLeft: () => {
        if (leftNeighbor !== undefined) {
          applyColumnReorder(table, columnId, leftNeighbor)
        }
      },
      onMoveRight: () => {
        if (rightNeighbor !== undefined) {
          applyColumnReorder(table, columnId, rightNeighbor)
        }
      },
    }
  }

  return {
    columnReorder: enabled
      ? {
          columnIds: reorderableColumnIds,
          sensors: columnDragSensors,
          onDragEnd: handleColumnDragEnd,
        }
      : undefined,
    getColumnMove,
  }
}
